import { useEffect, useState } from 'react';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Container,
  Input,
  Popover,
  PopoverBody,
  Spinner,
} from 'reactstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '@mdi/react';
import {
  mdiFileDownloadOutline,
  mdiDotsVertical,
  mdiFilterOutline,
} from '@mdi/js';
import Avatar from 'react-avatar';
import { toast } from 'react-toastify';
import './PerformanceMatrix.scss';
import { ExportUtil, ImageFileUtil } from '../../../../utils';
import ClearAllIcon from '../../../../assets/icons/clear-all.svg';
import EditRatingsModal from '../EditRatingsModal/EditRatingsModal';

function PerformanceMatrix({
  refetchAllData,
  calibrationMatrixData,
  calibrationMatrixIsPending,
  calibrationMatrixEmp,
  calibrationMatrixEmpIsPending,
  toggleFilter,
  filterData,
  resetFilterData,
  weightagePercentage,
  reviewPrd,
  isReadOnly,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const rows = [5, 4, 3, 2, 1]; // PERFORMANCE (vertical)
  const cols = [1, 2, 3, 4, 5]; // PCM (horizontal)
  const [openPCMPopoverId, setOpenPCMPopoverId] = useState(null);
  const [employeeList, setEmployeeList] = useState([]);
  const [matrix, setMatrix] = useState({});
  const [filterItem, setFilterItem] = useState({});
  const [modalEdit, setModalEdit] = useState(false);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedKey, setSelectedKey] = useState();

  useEffect(() => {
    setFilterItem(filterData);
  }, [filterData]);

  const removeFilterItem = (keyToReset, emptyValue) => {
    const updated = {
      ...filterItem,
      [keyToReset]: emptyValue,
    };
    setFilterItem(updated);
    resetFilterData(updated);
  };

  const clearFilter = () => {
    setFilterItem({});
    resetFilterData({});
  };

  useEffect(() => {
    if (calibrationMatrixData?.result?.length > 0) {
      const matrixData = {};
      calibrationMatrixData?.result?.forEach(mt => {
        matrixData[mt?.Rating_Combination] = mt?.TotalEmployees ?? 0;
      });
      setMatrix(matrixData);
    } else {
      setMatrix({});
    }
  }, [calibrationMatrixData]);
  const togglePCMPopover = id => {
    setOpenPCMPopoverId(prev => (prev === id ? null : id));
  };

  const handleViewPerformance = id => {
    navigate(`/hr/performance/${id}`, {
      state: { from: location.pathname, activeTab: 2 },
    });
    togglePCMPopover(id);
  };

  // Add handler function for checkbox changes
  const handleEmployeeSelect = (item, checked) => {
    setSelectedEmployees(prev => {
      if (checked) {
        return [...prev, item];
      }
      return prev.filter(emp => emp.EmpCd !== item.EmpCd);
    });
  };

  const toggle = key => {
    if (!key || !matrix[key]) {
      return;
    }
    const [pcm, performance] = key.split(':');
    if (calibrationMatrixEmp?.result?.length > 0) {
      const arr = [];
      calibrationMatrixEmp?.result
        ?.filter(
          item =>
            item?.Avg_Rating_PERFORMANCE === parseInt(performance, 10) &&
            item?.Avg_Rating_PCM === parseInt(pcm, 10),
        )
        .forEach(emp => {
          arr.push({
            EmpCd: emp?.EmpCd?.trim(),
            EmpName: emp?.EmpName?.trim(),
            ImageFile: emp?.ImageFile?.trim(),
            Avg_Rating_PCM: emp?.Avg_Rating_PCM,
            Avg_Rating_PERFORMANCE: emp?.Avg_Rating_PERFORMANCE,
          });
        });
      setEmployeeList(arr);
      setIsOpen(true);
      setSelectedKey(key);
    }
  };

  const getImage = item => {
    if (item?.ImageFile) {
      return ImageFileUtil.getEmployeeFilePath(item?.ImageFile);
    }
    return null;
  };

  // Export based on filters without pagination
  const handleExportExcel = async () => {
    const exportData = calibrationMatrixEmp?.result || [];
    if (!exportData || exportData.length === 0) {
      toast.error('No data found to export', {
        className: 'toast-error',
      });
      return;
    }
    const timesTamp = new Date().toISOString().slice(0, 10);

    const formattedData = exportData?.map(item => ({
      EmpCd: item?.EmpCd?.trim(),
      EmpName: item?.EmpName?.trim(),
      Avg_Rating_PERFORMANCE: item?.Avg_Rating_PERFORMANCE
        ? Math.round(item?.Avg_Rating_PERFORMANCE)
        : 'Rating not yet provided',
      Avg_Rating_PCM: item?.Avg_Rating_PCM
        ? Math.round(item?.Avg_Rating_PCM)
        : 'Rating not yet provided',
    }));

    ExportUtil.downloadExcel(formattedData, {
      fileName: `Performance_Matrix_${timesTamp}.xlsx`,
      columns: {
        EmpCd: 'Employee Code',
        EmpName: 'Employee Name',
        Avg_Rating_PERFORMANCE: 'Average Rating Performance',
        Avg_Rating_PCM: 'Average Rating PCM',
      },
    });
  };

  return (
    <Container
      className="d-flex flex-column flex-fill min-h-0"
      fluid
      style={{ position: 'relative' }}
    >
      <Card className="pc-card mt-3 d-flex flex-column flex-fill min-h-0">
        <CardHeader
          className="d-flex justify-content-between align-items-center pt-2 pb-2"
          style={{ borderRadius: '10px 10px 0 0' }}
        >
          <div>
            <span className="fs-5">
              <strong>PCM vs Performance</strong>
            </span>
          </div>
          <div className="d-flex gap-2">
            <div className="d-flex gap-1 align-items-center">
              {filterItem?.companyCode?.Code && (
                <div
                  key={filterItem.companyCode.Code}
                  className="filter-bubble px-2"
                >
                  <span className="filter-bubble-text">
                    {filterItem?.companyCode?.Description}
                  </span>
                  <span
                    className="filter-bubble-close"
                    onClick={() => removeFilterItem('companyCode', null)}
                  >
                    &times;
                  </span>
                </div>
              )}
              {filterItem?.employee?.name && (
                <div
                  key={filterItem.employee.name}
                  className="filter-bubble px-2"
                >
                  <span className="filter-bubble-text">
                    {filterItem.employee.name}
                  </span>
                  <span
                    className="filter-bubble-close"
                    onClick={() => removeFilterItem('employee', null)}
                  >
                    &times;
                  </span>
                </div>
              )}
              {filterItem?.manager?.name && (
                <div
                  key={filterItem.manager.name}
                  className="filter-bubble px-2"
                >
                  <span className="filter-bubble-text">
                    {filterItem.manager.name}
                  </span>
                  <span
                    className="filter-bubble-close"
                    onClick={() => removeFilterItem('manager', null)}
                  >
                    &times;
                  </span>
                </div>
              )}
              {filterItem?.department?.Description && (
                <div
                  key={filterItem?.department?.Description}
                  className="filter-bubble px-2"
                >
                  <span className="filter-bubble-text">
                    {filterItem?.department?.Description}
                  </span>
                  <span
                    className="filter-bubble-close"
                    onClick={() => removeFilterItem('department', null)}
                  >
                    &times;
                  </span>
                </div>
              )}
              {filterItem?.classification?.label && (
                <div
                  key={filterItem.classification.label}
                  className="filter-bubble px-2"
                >
                  <span className="filter-bubble-text">
                    {filterItem.classification.label}
                  </span>
                  <span
                    className="filter-bubble-close"
                    onClick={() => removeFilterItem('classification', null)}
                  >
                    &times;
                  </span>
                </div>
              )}
              {(filterItem.companyCode ||
                filterItem.manager ||
                filterItem.employee ||
                filterItem.department ||
                filterItem.classiffication) && (
                <div
                  className="clear-all-btn px-2"
                  onClick={() => clearFilter()}
                >
                  <span className="clear-all-btn-text">Clear All</span>
                  <img src={ClearAllIcon} alt="Clear All" />
                </div>
              )}
            </div>
            {(calibrationMatrixIsPending || calibrationMatrixEmpIsPending) && (
              <Spinner
                size="lg"
                color="primary"
                className="filter-dropdown-spinner"
              />
            )}
            <Button className="btn btn-light" onClick={() => toggleFilter()}>
              <span>Filter</span>
              <Icon path={mdiFilterOutline} size={0.8} />
            </Button>
            <Button className="btn btn-light" onClick={handleExportExcel}>
              <span>Report</span>
              <Icon path={mdiFileDownloadOutline} size={0.8} />
            </Button>
          </div>
        </CardHeader>
        <CardBody className="performance-pcm-body d-flex flex-column flex-fill min-h-0">
          <div className="pcm-x">Performance</div>
          <div className="performance-y">
            <div className="performance-y-title">
              <span>PCM</span>
            </div>
            <div className="performance-y-rating">
              <div className="blank"> </div>
              {rows.map(row => (
                <div className="rating" key={row}>
                  <div className="rating-name">{row}</div>
                </div>
              ))}
            </div>
            <div className="pcm-x-rating">
              <div className="rating-header">
                {cols.map(col => (
                  <div className="rating border-right" key={col}>
                    <div className="rating-name">{col}</div>
                  </div>
                ))}
              </div>
              <div className="rating-box">
                {cols.map(col => (
                  <div className="rating-col border-right">
                    {rows.map(row => {
                      const key = `${row}:${col}`;
                      const value = `${row}:${col}`;
                      return (
                        <Card onClick={() => toggle(key)}>
                          <div className="item">
                            <div className="ratio">{value}</div>
                            <div className="count">{matrix[key] ?? 0}</div>
                          </div>
                        </Card>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </CardBody>
      </Card>
      {isOpen && (
        <Card className="card-list">
          <CardHeader>
            <div className="d-flex flex-wrap align-items-center gap-2">
              {!isReadOnly && (
                <Button
                  disabled={selectedEmployees?.length === 0}
                  onClick={() => setModalEdit(true)}
                >
                  Edit
                </Button>
              )}
              <Button
                onClick={() => {
                  setIsOpen(false);
                  setSelectedKey(null);
                }}
                color="dark"
                className="btn-close"
              />
            </div>
          </CardHeader>
          <CardBody>
            <div className="items">
              {employeeList.map(item => (
                <div className="item">
                  <div className="detail">
                    <Avatar
                      size="32px"
                      name={item?.EmpName || 'Unknown'}
                      color="#CFCBDC"
                      fgColor="#475467"
                      maxInitials={2}
                      style={{
                        fontSize: '14px',
                        fontWeight: '600',
                      }}
                      src={getImage(item)}
                      round
                    />
                    <div className="info">
                      <div className="pcm-title">{item?.EmpCd}</div>
                      <div className="sub-title-pcm">
                        <strong>{item?.EmpName}</strong>
                      </div>
                    </div>
                  </div>
                  <div className="d-flex flex-wrap align-items-center gap-2">
                    {!isReadOnly && (
                      <Input
                        type="checkbox"
                        className="green-checkbox"
                        checked={selectedEmployees.some(
                          emp => emp.EmpCd === item.EmpCd,
                        )}
                        onChange={e =>
                          handleEmployeeSelect(item, e.target.checked)
                        }
                      />
                    )}
                    <Button
                      size="sm"
                      className="no-outline"
                      id={`popover-pcm-button-${item.EmpCd}`}
                      onClick={() => togglePCMPopover(item.EmpCd)}
                      outline
                    >
                      <Icon path={mdiDotsVertical} size={0.8} />
                    </Button>
                  </div>
                  <Popover
                    placement="right"
                    isOpen={openPCMPopoverId === item.EmpCd}
                    target={`popover-pcm-button-${item.EmpCd}`}
                    toggle={() => togglePCMPopover(item.EmpCd)}
                  >
                    <PopoverBody className="performance-popover-body">
                      <Button
                        className="btn-performance"
                        onClick={() => {
                          handleViewPerformance(item.EmpCd);
                        }}
                      >
                        View Performance
                      </Button>
                    </PopoverBody>
                  </Popover>
                </div>
              ))}
            </div>
          </CardBody>
        </Card>
      )}
      <EditRatingsModal
        isOpen={modalEdit}
        toggle={() => {
          setModalEdit(false);
          setIsOpen(false);
          setSelectedKey(null);
          setSelectedEmployees([]);
        }}
        selectedKey={selectedKey}
        selectedEmployees={selectedEmployees}
        weightagePercentage={weightagePercentage}
        reviewPrd={reviewPrd}
        refetchAllData={refetchAllData}
      />
    </Container>
  );
}

export default PerformanceMatrix;
